<template>
  <div class="renderPage rmc-container" :class="{ 'theme--dark': $vuetify.theme.dark }">

    <!-- TOOLBAR CABECERA -->
    <v-toolbar flat height="56" :color="$vuetify.theme.dark ? '#1e293b' : '#f1f5f9'" class="rmc-toolbar px-2">
      <v-icon class="mr-2" :color="$vuetify.theme.dark ? 'teal lighten-2' : 'blue-grey darken-3'">mdi-cash-multiple</v-icon>
      <v-toolbar-title class="font-weight-black text-uppercase rmc-toolbar-title" :class="$vuetify.theme.dark ? 'white--text' : 'blue-grey--text text--darken-4'">
        Movimientos de Caja
      </v-toolbar-title>
      <v-spacer />
      <v-chip v-if="esAdmin" small color="blue-grey darken-2" dark class="font-weight-bold">
        <v-icon x-small left>mdi-shield-account</v-icon>Vista Admin
      </v-chip>
    </v-toolbar>

    <!-- FILTROS -->
    <v-card flat class="rmc-filter-card mx-0" :color="$vuetify.theme.dark ? '#0f172a' : 'white'">
      <v-toolbar flat height="auto" :color="$vuetify.theme.dark ? '#0f172a' : 'white'" class="px-3 pt-2 pb-3">
        <v-row dense align="center">
          <!-- Fecha Inicio -->
          <v-col cols="12" sm="6" :md="esAdmin ? 3 : 4">
            <v-menu v-model="menuFechaInicio" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="fechaInicio"
                  label="Fecha Inicio"
                  prepend-inner-icon="mdi-calendar-start"
                  readonly outlined dense hide-details
                  class="rmc-search"
                  v-bind="attrs" v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker v-model="fechaInicio" no-title scrollable @input="menuFechaInicio = false"></v-date-picker>
            </v-menu>
          </v-col>

          <!-- Fecha Fin -->
          <v-col cols="12" sm="6" :md="esAdmin ? 3 : 4">
            <v-menu v-model="menuFechaFin" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="fechaFin"
                  label="Fecha Fin"
                  prepend-inner-icon="mdi-calendar-end"
                  readonly outlined dense hide-details
                  class="rmc-search"
                  v-bind="attrs" v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker v-model="fechaFin" no-title scrollable @input="menuFechaFin = false"></v-date-picker>
            </v-menu>
          </v-col>

          <!-- Selector vendedor (solo Admin) -->
          <v-col v-if="esAdmin" cols="12" sm="6" md="3">
            <v-select
              v-model="usuarioSeleccionado"
              :items="usuarios"
              item-text="nombre"
              item-value="id"
              label="Vendedor"
              prepend-inner-icon="mdi-account-outline"
              outlined dense hide-details clearable
              class="rmc-search"
            ></v-select>
          </v-col>

          <!-- Nombre vendedor (solo no-admin) -->
          <v-col v-else cols="12" sm="6" md="4" class="d-flex align-center">
            <v-icon small class="mr-2" :color="$vuetify.theme.dark ? 'blue-grey lighten-2' : 'blue-grey darken-3'">mdi-account-outline</v-icon>
            <div>
              <div class="caption" :class="$vuetify.theme.dark ? 'blue-grey--text text--lighten-1' : 'grey--text'">Vendedor</div>
              <div class="font-weight-bold text-body-2">{{ user.nombre }}</div>
            </div>
          </v-col>

          <!-- Botón Buscar -->
          <v-col cols="12" sm="6" :md="esAdmin ? 3 : 4">
            <v-btn color="blue-grey darken-2" dark depressed block height="40" class="rmc-btn font-weight-bold" :loading="loading" @click="buscarSesiones">
              <v-icon left size="16">mdi-magnify</v-icon> Buscar Sesiones
            </v-btn>
          </v-col>
        </v-row>
      </v-toolbar>
    </v-card>

    <!-- TABLA DE SESIONES -->
    <v-card flat class="rmc-table-card mx-0" :color="$vuetify.theme.dark ? '#0f172a' : 'white'">
      <v-toolbar flat height="52" :color="$vuetify.theme.dark ? '#0f172a' : 'white'" class="px-3">
        <v-icon small class="mr-2" :color="$vuetify.theme.dark ? 'teal lighten-2' : 'blue-grey darken-3'">mdi-timeline-clock-outline</v-icon>
        <span class="font-weight-black text-caption text-uppercase" style="letter-spacing:1px" :class="$vuetify.theme.dark ? 'white--text' : 'blue-grey--text text--darken-3'">Sesiones de Caja</span>
        <v-spacer />
        <v-chip v-if="sesiones.length > 0" x-small color="blue-grey darken-2" dark class="font-weight-bold">
          {{ sesiones.length }} resultado(s)
        </v-chip>
      </v-toolbar>

      <v-data-table
        :headers="headers"
        :items="sesiones"
        :loading="loading"
        loading-text="Buscando sesiones..."
        no-data-text="No hay sesiones para los filtros seleccionados."
        :expanded.sync="expanded"
        item-key="id"
        show-expand
        single-expand
        class="rmc-table"
        @item-expanded="cargarMovimientos"
      >
        <template v-slot:[`item.fecha`]="{ item }">
          <span class="font-weight-medium">{{ formatDate(item.fecha) }}</span>
        </template>

        <template v-slot:[`item.monto`]="{ item }">
          <span class="font-weight-bold" :class="$vuetify.theme.dark ? 'teal--text text--lighten-2' : 'teal--text text--darken-1'">₡ {{ formatMoney(item.monto) }}</span>
        </template>

        <template v-slot:[`item.cerrado`]="{ item }">
          <v-chip small :color="item.cerrado ? 'error' : 'success'" dark class="font-weight-bold">
            <v-icon x-small left>{{ item.cerrado ? 'mdi-lock' : 'mdi-lock-open-variant' }}</v-icon>
            {{ item.cerrado ? 'Cerrada' : 'Abierta' }}
          </v-chip>
        </template>

        <!-- Expand row con movimientos -->
        <template v-slot:expanded-item="{ headers, item }">
          <td :colspan="headers.length" class="pa-0">
            <v-card flat class="ma-2 rmc-expand-card">
              <!-- Header de la expansion -->
              <div class="rmc-expand-header d-flex align-center px-4 py-2" :class="$vuetify.theme.dark ? 'rmc-expand-header--dark' : ''">
                <v-icon small class="mr-2" color="white">mdi-swap-vertical</v-icon>
                <span class="white--text font-weight-black text-caption text-uppercase" style="letter-spacing:1px">
                  Movimientos de la sesión #{{ item.id }}
                </span>
              </div>

              <!-- Loading -->
              <div v-if="loadingMovs" class="pa-4">
                <v-progress-linear indeterminate color="blue-grey darken-2" rounded height="3"></v-progress-linear>
              </div>

              <!-- Vacío -->
              <div v-else-if="!movimientosPorSesion[item.id] || movimientosPorSesion[item.id].length === 0" class="pa-6 text-center">
                <v-icon size="40" :color="$vuetify.theme.dark ? 'blue-grey darken-1' : 'grey lighten-1'" class="mb-2">mdi-inbox-outline</v-icon>
                <p class="caption" :class="$vuetify.theme.dark ? 'blue-grey--text' : 'grey--text'">No se registraron movimientos en esta sesión.</p>
              </div>

              <!-- Tabla de movimientos -->
              <v-simple-table v-else dense class="rmc-inner-table">
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th style="width:40px"></th>
                      <th>Descripción</th>
                      <th>Fecha</th>
                      <th class="text-right">Monto</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="mov in movimientosPorSesion[item.id]" :key="mov.id">
                      <td>
                        <v-icon small :color="mov.tipo === 1 ? 'success' : 'error'">
                          {{ mov.tipo === 1 ? 'mdi-arrow-down-bold' : 'mdi-arrow-up-bold' }}
                        </v-icon>
                      </td>
                      <td class="font-weight-medium">{{ mov.descripcion || 'Sin descripción' }}</td>
                      <td class="caption">{{ formatDate(mov.fecha) }}</td>
                      <td class="text-right font-weight-black" :class="mov.tipo === 1 ? 'success--text' : 'error--text'">
                        {{ mov.tipo === 1 ? '+' : '-' }} ₡{{ formatMoney(mov.monto) }}
                      </td>
                    </tr>
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colspan="3" class="text-right caption font-weight-bold" :class="$vuetify.theme.dark ? 'blue-grey--text text--lighten-1' : 'blue-grey--text text--darken-2'">
                        Ingresos: <span class="success--text">₡{{ formatMoney(movimientosPorSesion[item.id].filter(m => m.tipo === 1).reduce((a, b) => a + b.monto, 0)) }}</span>
                        &nbsp;&nbsp;
                        Salidas: <span class="error--text">₡{{ formatMoney(movimientosPorSesion[item.id].filter(m => m.tipo === 2).reduce((a, b) => a + b.monto, 0)) }}</span>
                      </td>
                      <td></td>
                    </tr>
                  </tfoot>
                </template>
              </v-simple-table>
            </v-card>
          </td>
        </template>

        <template v-slot:no-data>
          <div class="pa-8 text-center">
            <v-icon size="48" :color="$vuetify.theme.dark ? 'blue-grey darken-1' : 'grey lighten-1'" class="mb-3">mdi-cash-off</v-icon>
            <p :class="$vuetify.theme.dark ? 'blue-grey--text' : 'grey--text'">No hay sesiones para el período seleccionado.</p>
            <v-btn color="primary" depressed small @click="buscarSesiones">Reintentar</v-btn>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <Snackbar v-bind:text="snackText" v-bind:snackbar.sync="snackValue" />
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { CashService } from "../services/CashService";
import axios from "axios";
import Snackbar from "./shared/Snackbar.vue";

export default {
  name: "ReporteMovimientosCaja",
  components: { Snackbar },
  data() {
    const today = new Date().toISOString().substr(0, 10);
    return {
      fechaInicio: today,
      fechaFin: today,
      menuFechaInicio: false,
      menuFechaFin: false,
      usuarioSeleccionado: null,
      usuarios: [],
      sesiones: [],
      expanded: [],
      movimientosPorSesion: {},
      loading: false,
      loadingMovs: false,
      snackText: "",
      snackValue: false,
      headers: [
        { text: "ID", value: "id", width: 80 },
        { text: "Fecha Apertura", value: "fecha" },
        { text: "Monto Apertura", value: "monto" },
        { text: "Estado", value: "cerrado", width: 120 },
        { text: "", value: "data-table-expand" },
      ],
    };
  },
  computed: {
    ...mapGetters(["user", "strCia"]),
    esAdmin() {
      if (!this.user) return false;
      const tipo = this.user.tipo !== undefined ? this.user.tipo : this.user.Tipo;
      return tipo == 1;
    },
  },
  created() {
    if (this.esAdmin) {
      this.cargarUsuarios();
    } else {
      this.usuarioSeleccionado = this.user.id;
    }
    this.buscarSesiones();
  },
  methods: {
    async cargarUsuarios() {
      try {
        const res = await axios.get(`api/Usuarios/getUsuariosByCia?cia=${this.strCia}`);
        this.usuarios = res.data;
      } catch (e) {
        console.error("Error cargando usuarios:", e);
      }
    },
    async buscarSesiones() {
      this.loading = true;
      this.sesiones = [];
      this.expanded = [];
      this.movimientosPorSesion = {};
      try {
        const uid = this.esAdmin ? this.usuarioSeleccionado : this.user.id;
        this.sesiones = await CashService.getSesionesCaja(this.strCia, this.fechaInicio, this.fechaFin, uid);
      } catch (e) {
        this.snackText = "Error al buscar sesiones: " + e.message;
        this.snackValue = true;
      } finally {
        this.loading = false;
      }
    },
    async cargarMovimientos({ item, value }) {
      if (!value) return;
      if (this.movimientosPorSesion[item.id]) return;
      this.loadingMovs = true;
      try {
        const movs = await CashService.getMovimientosCaja(this.strCia, item.id);
        this.$set(this.movimientosPorSesion, item.id, movs);
      } catch (e) {
        this.$set(this.movimientosPorSesion, item.id, []);
        this.snackText = "Error cargando movimientos.";
        this.snackValue = true;
      } finally {
        this.loadingMovs = false;
      }
    },
    formatDate(dateStr) {
      if (!dateStr) return "-";
      return new Date(dateStr).toLocaleString("es-CR", {
        day: "2-digit", month: "2-digit", year: "numeric",
        hour: "2-digit", minute: "2-digit"
      });
    },
    formatMoney(val) {
      if (val == null) return "0";
      return Number(val).toLocaleString("es-CR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
  },
};
</script>

<style scoped>
/* ===== CONTAINER ===== */
.rmc-container {
  background-color: #f1f5f9;
  min-height: 100vh;
}
.theme--dark.rmc-container {
  background-color: #0f172a;
}

/* ===== TOOLBAR ===== */
.rmc-toolbar {
  border-bottom: 1px solid rgba(0, 0, 0, 0.1) !important;
}
.theme--dark .rmc-toolbar {
  border-bottom: 1px solid rgba(255, 255, 255, 0.07) !important;
}
.rmc-toolbar-title {
  font-size: 0.95rem !important;
  letter-spacing: 1.5px;
}

/* ===== BUTTONS ===== */
.rmc-btn {
  border-radius: 4px !important;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  font-size: 0.75rem !important;
}

/* ===== FILTER & TABLE CARDS ===== */
.rmc-filter-card {
  border-bottom: 1px solid rgba(0, 0, 0, 0.06) !important;
}
.theme--dark .rmc-filter-card {
  border-bottom: 1px solid rgba(255, 255, 255, 0.05) !important;
}
.rmc-table-card {
  border-bottom: 1px solid rgba(0, 0, 0, 0.08) !important;
}

/* ===== SEARCH INPUTS ===== */
.rmc-search >>> .v-input__slot {
  border-radius: 4px !important;
}

/* ===== TABLE ===== */
.rmc-table { background: transparent !important; }
.rmc-table >>> thead th {
  background-color: #37474f !important;
  color: #ffffff !important;
  text-transform: uppercase;
  font-size: 0.72rem !important;
  font-weight: 700 !important;
  letter-spacing: 0.07em;
  border-bottom: none !important;
}
.rmc-table >>> tbody tr:hover {
  background-color: rgba(55, 71, 79, 0.05) !important;
}
.theme--dark .rmc-table >>> tbody tr:hover {
  background-color: rgba(255, 255, 255, 0.05) !important;
}

/* ===== EXPANDED ROW ===== */
.rmc-expand-card {
  border: 1px solid rgba(0, 0, 0, 0.07) !important;
  border-radius: 4px !important;
  overflow: hidden;
}
.theme--dark .rmc-expand-card {
  border-color: rgba(255, 255, 255, 0.07) !important;
}

.rmc-expand-header {
  background-color: #455a64;
  min-height: 40px;
}
.rmc-expand-header--dark {
  background-color: #263238 !important;
}

/* ===== INNER TABLE (movimientos) ===== */
.rmc-inner-table { background: transparent !important; }
.rmc-inner-table >>> thead th {
  background-color: #37474f !important;
  color: #ffffff !important;
  text-transform: uppercase;
  font-size: 0.7rem !important;
  font-weight: 700 !important;
  letter-spacing: 0.07em;
}
.rmc-inner-table >>> tfoot td {
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  padding: 10px 16px;
}
.theme--dark .rmc-inner-table >>> tfoot td {
  border-top-color: rgba(255, 255, 255, 0.07);
}
</style>
